import { BdsButton, BdsGrid, BdsIllustration, BdsLoadingSpinner, BdsTypo } from 'blip-ds/dist/blip-ds-react/components';
import { Option } from 'blip-ds/dist/types/components/selects/select-interface';
import { Modal } from 'components/Modal';
import { SelectChips } from 'components/SelectChips';
import { useAppContext } from 'contexts/AppContext';
import { Installation } from 'interfaces';
import { Tags } from 'interfaces/Tags';
import React, { Dispatch, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createToastError, createToastSuccess } from 'services/Toast';
import { TicketsResponse } from 'typings';
import './styles.scss';
interface TicketFinishModalProps {
    isOpen: boolean;
    ticket: TicketsResponse['tickets'][number];
    selectedBotInfo: Installation;
    setOpen: Dispatch<React.SetStateAction<boolean>>;
    onTicketChanged?: () => void;
}

export const TicketFinishingModal: React.FC<TicketFinishModalProps> = ({
    isOpen,
    setOpen,
    ticket,
    selectedBotInfo: { installationId, tenantId },
    onTicketChanged,
}) => {
    const { t } = useTranslation();
    const {
        appServices: { goPersonalService },
    } = useAppContext();
    const [tags, setTags] = useState<Tags>();
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const fetchTags = useCallback(async () => {
        if (ticket) {
            setIsLoadingTags(true);
            try {
                const tagsResponse = await goPersonalService.getTags(tenantId, ticket.botId);
                setTags(tagsResponse);
            } catch {
                await createToastError(
                    t('go-personal-finish-modal-error-load-tags'),
                    t('go-personal-finish-modal-error-retry'),
                );
                setOpen(false);
            } finally {
                setIsLoadingTags(false);
            }
        }
    }, [ticket, tenantId, goPersonalService, setOpen, t]);

    useEffect(() => {
        if (isOpen) {
            setSelectedTags([]);
            fetchTags();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const closeTickets = async () => {
        setIsSubmitting(true);
        try {
            await goPersonalService.patchTickets(installationId, ticket.id, {
                tags: selectedTags,
            });
            await createToastSuccess('', t('go-personal-finish-modal-success', { ticketNumber: ticket.number }));
            setOpen(false);
            onTicketChanged?.();
        } catch {
            await createToastError(
                t('go-personal-finish-modal-error-finish', { ticketNumber: ticket.number }),
                t('go-personal-finish-modal-error-retry'),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            size="dynamic"
            key={ticket?.id}
            open={isOpen}
            onClose={() => setOpen(!isOpen)}
            closeButton
            outzoneClose={false}
            enterClose={false}
            footer={
                <BdsGrid style={{ width: '100%' }} justifyContent="flex-end" gap="1">
                    <BdsButton variant="secondary" onClick={() => setOpen(false)}>
                        {t('go-personal-finish-modal-cancel')}
                    </BdsButton>
                    <BdsButton
                        variant="primary"
                        disabled={
                            isLoadingTags ||
                            isSubmitting ||
                            !tags ||
                            (tags?.isTagsRequired === true && tags?.hasTags === true && selectedTags.length === 0)
                        }
                        onClick={async () => {
                            if (tags?.isTagsRequired === true && tags?.hasTags === true && selectedTags.length === 0)
                                return;
                            await closeTickets();
                        }}
                    >
                        {t('go-personal-finish-modal-finish')}
                    </BdsButton>
                </BdsGrid>
            }
        >
            <BdsGrid container gap="4">
                <BdsGrid height="200px">
                    <BdsIllustration name="letter-check-okay" type="spots" />
                </BdsGrid>
                <BdsGrid direction="column" gap="2" margin="t-3">
                    <BdsTypo variant="fs-20" bold="bold" className="ticket-finishing-typo">
                        {t('go-personal-finish-modal-title', { ticketNumber: ticket?.number })}
                    </BdsTypo>
                    {isLoadingTags ? (
                        <BdsGrid justifyContent="center" padding="y-2">
                            <BdsLoadingSpinner size="small" />
                        </BdsGrid>
                    ) : (
                        <SelectChips
                            label={t('go-personal-finish-modal-add-tags')}
                            options={
                                tags?.tags.map(({ text: tag }) => ({ label: tag, value: tag })) ?? ([] as Option[])
                            }
                            handleChange={(chips: string[]) => setSelectedTags(chips)}
                            disabled={!tags?.hasTags}
                        />
                    )}
                </BdsGrid>
            </BdsGrid>
        </Modal>
    );
};
